import { z } from 'zod'

export const workspaceSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
})

export const createWorkspaceRequestSchema = z.object({
  name: z.string().min(1),
  metadata: z.record(z.unknown()).optional(),
  // The server derives the owner from the caller's proven identity; ownerId is only a cross-check.
  ownerId: z.string().uuid().optional(),
  loginToken: z.string().min(1).optional(),
})

export const createWorkspaceResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  metadata: z.record(z.unknown()).nullable().optional(),
  ownerId: z.string().uuid(),
  accessToken: z.string(),
  superUser: z.boolean().default(false),
  developerRole: z.boolean().default(false),
})

export const workspaceUserRoleRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  roleType: z.string(),
  active: z.boolean(),
})

/**
 * A workspace member. `roles`/`roleRefs` are empty unless the caller may view users or roles.
 * `superUser` is a legacy alias of `owner`.
 */
export const workspaceUserSchema = z.object({
  userId: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
  superUser: z.boolean(),
  owner: z.boolean().default(false),
  admin: z.boolean().default(false),
  roles: z.array(z.string()),
  roleRefs: z.array(workspaceUserRoleRefSchema).default([]),
})

export const inviteUserRequestSchema = z.object({
  email: z.string().email(),
})

export const inviteUserResponseSchema = z.object({
  inviteLink: z.string(),
})

export const acceptInviteRequestSchema = z.object({
  token: z.string().min(1),
})

export type Workspace = z.infer<typeof workspaceSchema>
export type CreateWorkspaceRequest = z.infer<typeof createWorkspaceRequestSchema>
export type CreateWorkspaceResponse = z.infer<typeof createWorkspaceResponseSchema>
export type WorkspaceUser = z.infer<typeof workspaceUserSchema>
export type WorkspaceUserRoleRef = z.infer<typeof workspaceUserRoleRefSchema>
export type InviteUserRequest = z.infer<typeof inviteUserRequestSchema>
export type InviteUserResponse = z.infer<typeof inviteUserResponseSchema>
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequestSchema>

// Task 13 — invite info + reject
export const inviteStatusSchema = z.enum(['PENDING', 'ACCEPTED', 'REJECTED', 'SUPERSEDED'])

export const inviteInfoResponseSchema = z.object({
  email: z.string().email(),
  workspaceName: z.string(),
  status: inviteStatusSchema,
  expired: z.boolean(),
})

export const rejectInviteRequestSchema = z.object({
  token: z.string().min(1),
})

export type InviteStatus = z.infer<typeof inviteStatusSchema>
export type InviteInfoResponse = z.infer<typeof inviteInfoResponseSchema>
export type RejectInviteRequest = z.infer<typeof rejectInviteRequestSchema>
